/**
 * The buyer's billing region — £ UK / € Europe / $ everyone else — as the
 * SERVER resolves it from the caller's location (Cloudflare geolocation via
 * GET /billing/currency). The client never derives this from the device
 * locale: locale is language, and "English (UK)" showed pounds from anywhere.
 *
 * One fetch per app session, cached module-wide: every screen that renders a
 * price shares the same answer.
 */
import { useEffect, useState } from "react";

import { billingApi } from "@/api";
import type { LocalPlusCurrency } from "@/constants/plans";

export type BillingRegion = {
  currency: LocalPlusCurrency;
  /** ISO country the server geolocated, when it could ("GB", "DE", …). */
  country: string | null;
};

// USD is the "everyone else" bucket, so it's also the answer while we don't
// know. Every Plus price is the same number in every currency, so the moment
// of not-knowing can only ever show a different symbol, never a wrong amount.
const FALLBACK: BillingRegion = { currency: "USD", country: null };

let cached: BillingRegion | null = null;
let inflight: Promise<BillingRegion> | null = null;

function normalizeRegion(res: {
  currency?: string;
  country?: string | null;
}): BillingRegion {
  const code = res.currency?.toUpperCase();
  return {
    currency: code === "GBP" || code === "EUR" ? code : "USD",
    country: res.country ?? null,
  };
}

export async function fetchBillingRegion(): Promise<BillingRegion> {
  if (cached) return cached;
  if (!inflight) {
    inflight = billingApi.currency().then(
      (res) => (cached = normalizeRegion(res)),
      () => {
        // Unreachable API prices as USD for this render. The failure is NOT
        // cached — the next caller asks again.
        inflight = null;
        return FALLBACK;
      },
    );
  }
  return inflight;
}

/** The billing region, updating in place once the server has answered. */
export function useBillingRegion(): BillingRegion {
  const [region, setRegion] = useState<BillingRegion | null>(cached);

  useEffect(() => {
    if (cached) return;
    let live = true;
    void fetchBillingRegion().then((resolved) => {
      if (live) setRegion(resolved);
    });
    return () => {
      live = false;
    };
  }, []);

  return region ?? FALLBACK;
}

/** Test-only: forget the cached region so a spec can exercise the fetch. */
export function resetBillingRegionForTests(): void {
  cached = null;
  inflight = null;
}
